/**
 * This module provides functions in relation to calling API routes on the backend server and processing the incoming
 * responses. The functions in this module refer to the API endpoint ApiEndPoints.CASH_RESERVE.
 * @module
 */

import {ApiEndPoints, HttpMethods} from "../base/Settings";
import type {
    FetchRequest,
    CashReserveInput,
    CashReserve,
    UUID,
    WaterfallItemInput
} from "../base/ui_general/GeneralInterfaces";


/**
 * Creates a FetchRequest object for creating a new cash reserve, including both the cash reserve and its associated waterfall item.
 * @param cashReserveInput An object of type CashReserveInput containing the input data for creating the cash reserve.
 * @param waterfallItemInput An object of type WaterfallItemInput containing the input data for the associated waterfall item.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_create_cash_reserve(cashReserveInput: CashReserveInput, waterfallItemInput: WaterfallItemInput): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.CASH_RESERVE,
        method: HttpMethods.POST,
        query_parameters: {},
        body: {
            waterfall_item: waterfallItemInput,
            cash_reserve: cashReserveInput
        },
    }
}

/**
 * Creates a FetchRequest object populated with the parameters necessary for updating an existing
 * cash_reserve on the server.
 * @param cash_reserve An object of type CashReserve containing the updated cash_reserve data.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_update_cash_reserve(cash_reserve: CashReserve): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.CASH_RESERVE,
        method: HttpMethods.PUT,
        query_parameters: {},
        body: cash_reserve,
    };
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the query of cash_reserves
 * on the server.
 * @param transaction_id .The UUID of the transaction to query for.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_get_all_cash_reserves(transaction_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.CASH_RESERVE,
        method: HttpMethods.GET,
        query_parameters: { transaction_id: transaction_id },
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for deleting an existing
 * cash_reserve on the server.
 * @param cash_reserve_id The UUID of the cash_reserve to delete.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_delete_cash_reserve(cash_reserve_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.CASH_RESERVE,
        method: HttpMethods.DELETE,
        query_parameters: { cash_reserve_id: cash_reserve_id },
    };
}
